import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function EntreprisesPage() {
  // SEO Configuration
  useSEO({
    title: 'Entreprises & Professionnels - Délégation Présentations PowerPoint | Izislide',
    description: 'Imagez vos idées : Designer PowerPoint pour entreprises et professionnels. Déléguez vos présentations PowerPoint internes et gagnez du temps. Création PPT professionnelle rapide.',
    keywords: 'présentation powerpoint entreprise, délégation ppt, designer powerpoint, création présentation professionnelle, powerpoint professionnel',
    canonical: '/entreprises',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Délégation Présentations PowerPoint pour Entreprises & Professionnels',
      description: 'Service de création de présentations PowerPoint pour entreprises et professionnels',
      provider: {
        '@type': 'Organization',
        name: 'Izislide',
        slogan: 'Imagez vos idées'
      },
      areaServed: 'FR',
      serviceType: 'Création de présentations PowerPoint professionnelles'
    }
  });

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Offset pour le header fixe
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-24 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.85) 0%, rgba(0,143,92,0.85) 100%), url('https://public.readdy.ai/ai/img_res/f0724f06bd9f4576d1375685e2d2c774.jpg')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Entreprises & Professionnels : Libérez du temps, déléguez vos présentations PowerPoint internes
            </h1>
            <p className="text-xl md:text-2xl mb-10 opacity-90">
              <strong>Imagez vos idées</strong> : Notre designer PowerPoint transforme vos contenus en présentations PPT impactantes
            </p>
            <a 
              href="#solution" 
              onClick={(e) => scrollToSection(e, 'solution')}
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Oui, je délègue mes présentations PowerPoint
            </a>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Le quotidien des entreprises & professionnels
            </h2>
            <div className="bg-white p-10 rounded-xl shadow-lg border-l-4 border-red-400">
              <div className="space-y-6 text-lg text-gray-700">
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vous passez des <strong>heures sur PowerPoint</strong> alors que ce n'est pas votre métier</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vos <strong>présentations PPT internes</strong> manquent d'impact visuel et de clarté</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Les réunions s'éternisent car vos <strong>slides PowerPoint</strong> ne sont pas assez claires</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vous aimeriez déléguer mais ne savez pas à qui confier vos <strong>présentations PowerPoint</strong></span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="solution" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Déléguez vos présentations PowerPoint à notre designer PowerPoint
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-gray-50 p-8 rounded-xl">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-time-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">+5h par semaine</h3>
                <p className="text-gray-700">
                  Récupérez du temps précieux en déléguant vos <strong>présentations PowerPoint</strong> à notre designer PowerPoint expert
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-message-3-line text-3xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Messages clairs</h3>
                <p className="text-gray-700">
                  Vos idées complexes deviennent des <strong>présentations PPT</strong> limpides et convaincantes. <strong>Imagez vos idées</strong> !
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-line-chart-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Productivité boostée</h3>
                <p className="text-gray-700">
                  Concentrez-vous sur votre cœur de métier pendant que nous créons vos <strong>présentations PowerPoint</strong>
                </p>
              </div>
            </div>

            {/* Highlight Reel */}
            <div className="bg-[#C9D86A]/10 p-10 rounded-xl mb-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Concrètement, qu'est-ce que ça change pour votre entreprise ?
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Réunions plus fluides</h4>
                    <p className="text-gray-700">Vos <strong>présentations PowerPoint</strong> sont claires, vos réunions durent 30% moins longtemps</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Pitchs plus convaincants</h4>
                    <p className="text-gray-700">Vos <strong>présentations PPT commerciales</strong> convertissent mieux grâce au design PowerPoint professionnel</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Image professionnelle renforcée</h4>
                    <p className="text-gray-700">Vos <strong>présentations PowerPoint</strong> reflètent enfin votre niveau d'expertise</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Gain de temps massif</h4>
                    <p className="text-gray-700">+5h par semaine récupérées en déléguant vos <strong>présentations PowerPoint</strong></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Process */}
            <div className="bg-white p-10 rounded-xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Comment ça marche pour votre entreprise ?
              </h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Vous nous envoyez votre contenu</h4>
                    <p className="text-gray-700">Notes, documents Word, ancien PPT - peu importe le format de départ</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Notre designer PowerPoint structure et design</h4>
                    <p className="text-gray-700">Clarification des messages, design professionnel, storytelling efficace. <strong>Imagez vos idées</strong> devient réalité</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C9D86A] rounded-full flex items-center justify-center text-gray-900 font-bold text-xl mr-6 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Vous recevez votre présentation PowerPoint finalisée</h4>
                    <p className="text-gray-700">Livraison sous 48-72h, prête à présenter, 2 allers-retours inclus</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Des entreprises & professionnels qui nous font confiance
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  SC
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Sophie Chen</div>
                  <div className="text-sm text-gray-600">Directrice Marketing, PME Tech</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Je gagne <strong>6h par semaine</strong> depuis que je délègue mes <strong>présentations PowerPoint</strong> à Izislide. Leur designer PowerPoint comprend parfaitement nos enjeux business."
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  PD
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Pierre Durand</div>
                  <div className="text-sm text-gray-600">Consultant Indépendant</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Mes <strong>présentations PowerPoint clients</strong> sont maintenant au niveau de mon expertise. <strong>Imagez vos idées</strong>, c'est exactement ce dont j'avais besoin !"
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-r from-[#00A86B] to-[#008f5c]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à déléguer vos présentations PowerPoint ?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Gagnez du temps et boostez l'impact de vos présentations PPT avec notre designer PowerPoint
            </p>
            <Link 
              to="/contact" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Oui, je délègue mes présentations PowerPoint
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}